import { faRightFromBracket } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  ListItem,
  RadioButton,
  TextButton,
  Toast,
  Tooltip,
  ValidationMessage,
} from '@modusign/design-system';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form-v7';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { v4 } from 'uuid';

import { AddressBookContact, AddressBookSearchInput } from '@app/features/addressBook';
import { validateEmail } from '@app/libs/validation';

import { Signer } from '../../types/ExternalDocument';
import DetailInfoItem from '../DetailInfoItem';
import InternationalTelephoneInput from '../InternationalTelephoneInput';

function validateContact(
  value: { email: string; phone: string } | null,
  isIndividual: boolean
): string | true {
  if (!value) {
    return true;
  }

  const { email, phone } = value;

  let emailError = '';
  let phoneError = '';

  if (!email && !phone) {
    emailError = `${isIndividual ? '서명자' : '회사'} 이메일을 입력해 주세요.`;
    phoneError = `${isIndividual ? '서명자' : '회사'} 번호를 입력해 주세요.`;
  }

  const emailValidation = validateEmail(email);
  if (email && !emailValidation.success) {
    emailError = emailValidation.message;
  }

  if (phone && !isValidPhoneNumber(phone)) {
    phoneError = '정확한 번호를 입력해 주세요.';
  }

  if (emailError || phoneError) {
    return [emailError, phoneError].join('|');
  }

  return true;
}

export interface SignerFormState extends Omit<Signer, 'email' | 'phone'> {
  contact: {
    email?: string;
    phone?: string;
  } | null;
}

const SIGNER_LIMIT = 20;

export interface ParticipantFormState {
  participants: Record<string, SignerFormState>;
}

interface ParticipantsEditorProps {
  participantIds: string[];
  getPersonInCharge: () => {
    name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  setParticipantIds: (ids: string[]) => void;
}

export default function ParticipantsEditor({
  getPersonInCharge,
  participantIds,
  setParticipantIds,
}: ParticipantsEditorProps) {
  const canAddSigner = participantIds.length < SIGNER_LIMIT;

  return (
    <Box>
      {participantIds.map((id, idx) => (
        <ParticipantEditor
          getPersonInCharge={getPersonInCharge}
          id={id}
          index={idx}
          key={id}
          onRemove={() => setParticipantIds(participantIds.filter((v) => v !== id))}
        />
      ))}
      <Tooltip
        content={`최대 ${SIGNER_LIMIT}명까지 추가할 수 있습니다.`}
        isOpen={canAddSigner ? false : undefined}
        placement="top"
      >
        <Button
          disabled={!canAddSigner}
          mt={7}
          variant="subtle"
          width="100%"
          onClick={() => setParticipantIds([...participantIds, v4()])}
        >
          서명자 추가하기
        </Button>
      </Tooltip>
    </Box>
  );
}

interface ParticipantEditorProps {
  getPersonInCharge: () => {
    name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  index: number;
  id: string;
  onRemove: () => void;
}

function ParticipantEditor({ getPersonInCharge, index, id, onRemove }: ParticipantEditorProps) {
  const {
    control,
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<ParticipantFormState>();

  const isIndividual = watch(`participants.${id}.type`) !== 'CORPORATION';
  const externalSigner = watch(`participants.${id}.externalSigner`);

  const handleChangeContacts = useCallback(
    (selectedAddressBookContact: AddressBookContact) => {
      setValue(`participants.${id}.name`, selectedAddressBookContact.name);
      trigger(`participants.${id}.name`);
      setValue(`participants.${id}.company`, selectedAddressBookContact.companyName);
      trigger(`participants.${id}.company`);

      setValue(`participants.${id}.contact`, {
        email: selectedAddressBookContact.email ?? '',
        phone: selectedAddressBookContact.phoneNumber
          ? `+82${selectedAddressBookContact.phoneNumber.slice(1)}`
          : '',
      });
      trigger(`participants.${id}.contact`);
    },
    [id, setValue, trigger]
  );

  const participantTypeFieldProps = register(`participants.${id}.type`);

  return (
    <Box as="li" mt={7}>
      <Flex alignItems="center" justifyContent="space-between">
        <Heading as="div" fontSize="sm" fontWeight="medium">
          {index + 1}
        </Heading>
        <TextButton sx={{ textDecoration: 'none' }} onClick={onRemove}>
          삭제
        </TextButton>
      </Flex>
      <Flex
        borderTop="1px solid"
        borderTopColor="lightGray300"
        flexDirection="column"
        minHeight="60px"
        mt={4}
      >
        <DetailInfoItem name="주체">
          <Flex alignItems="center" as="label" display="flex" fontWeight="medium" gap={3}>
            <RadioButton
              defaultChecked
              {...participantTypeFieldProps}
              value="INDIVIDUAL"
              onClick={(event) => participantTypeFieldProps.onChange(event)}
            />
            <span>개인</span>
          </Flex>
          <Flex alignItems="center" as="label" display="flex" fontWeight="medium" gap={3} ml={4}>
            <RadioButton
              {...participantTypeFieldProps}
              value="CORPORATION"
              onClick={(event) => participantTypeFieldProps.onChange(event)}
            />
            <span>회사</span>
          </Flex>
        </DetailInfoItem>
        <Controller
          defaultValue={false}
          name={`participants.${id}.externalSigner`}
          render={({ field: { value, onChange } }) => (
            <DetailInfoItem name="구분">
              <Flex alignItems="center" as="label" display="flex" fontWeight="medium" gap={3}>
                <RadioButton checked={!value} onClick={() => onChange(false)} />
                <span>우리측</span>
              </Flex>
              <Flex
                alignItems="center"
                as="label"
                display="flex"
                fontWeight="medium"
                gap={3}
                ml={4}
              >
                <RadioButton checked={value} onClick={() => onChange(true)} />
                <span>상대측</span>
              </Flex>
            </DetailInfoItem>
          )}
        />
        <Box display={isIndividual ? 'none' : 'inline'}>
          <DetailInfoItem name="회사 이름" required>
            <Box width="100%">
              <Controller
                control={control}
                name={`participants.${id}.company`}
                render={({ field: { onChange, value } }) => (
                  <AddressBookSearchInput
                    initialValue={value ?? undefined}
                    isInvalid={!!errors.participants?.[id]?.company?.message}
                    maxLength={31}
                    placeholder="회사 이름"
                    searchTarget={['companyName']}
                    onChange={(value) => onChange(value)}
                    onSelected={handleChangeContacts}
                  />
                )}
                rules={{
                  required: isIndividual ? false : '회사 이름을 입력해 주세요',
                  maxLength: { value: 30, message: '최대 입력 가능한 길이는 30자입니다.' },
                }}
              />
              {errors.participants?.[id]?.company && (
                <ValidationMessage>{errors.participants?.[id]?.company?.message}</ValidationMessage>
              )}
            </Box>
          </DetailInfoItem>
        </Box>
        <DetailInfoItem name="서명자 이름" required>
          <Box width="100%">
            <Controller
              control={control}
              name={`participants.${id}.name`}
              render={({ field: { onChange, value } }) => (
                <AddressBookSearchInput
                  initialValue={value}
                  isInvalid={!!errors.participants?.[id]?.name?.message}
                  maxLength={31}
                  placeholder="이름"
                  searchTarget={['name']}
                  onChange={onChange}
                  onSelected={handleChangeContacts}
                />
              )}
              rules={{
                required: '서명자 이름을 입력해 주세요',
                maxLength: { value: 30, message: '최대 입력 가능한 길이는 30자입니다.' },
              }}
            />
            {errors.participants?.[id]?.name && (
              <ValidationMessage>{errors.participants?.[id]?.name?.message}</ValidationMessage>
            )}
          </Box>
        </DetailInfoItem>
        <DetailInfoItem name={`${!isIndividual ? '회사/' : ''}서명자 연락처`} required>
          <Box width="100%">
            <Controller
              defaultValue={{ email: '', phone: '' }}
              name={`participants.${id}.contact`}
              render={({ field: { onChange, value } }) => {
                const [emailError, phoneError] =
                  errors.participants?.[id]?.contact?.message?.split('|') || [];

                return (
                  <Flex flexDirection={['column', null, 'row']}>
                    <Box flex="1 1 0" mb={[3, null, 0]} mr={[null, null, 3]}>
                      <Input
                        isInvalid={!!emailError}
                        placeholder="이메일 주소"
                        value={value?.email}
                        onChange={(e) => onChange({ ...value, email: e.target.value })}
                      />
                      {emailError && <ValidationMessage>{emailError}</ValidationMessage>}
                    </Box>
                    <Box flex="1 1 0">
                      <InternationalTelephoneInput
                        isInvalid={!!phoneError}
                        value={value?.phone}
                        onChange={(v) => {
                          onChange({ ...value, phone: v });
                        }}
                      />
                      {phoneError && <ValidationMessage>{phoneError}</ValidationMessage>}
                    </Box>
                  </Flex>
                );
              }}
              rules={{ validate: (value) => validateContact(value, isIndividual) }}
            />

            <ul>
              <ListItem color="textNeutralLight" sx={{ lineHeight: 1.5, mt: 3 }}>
                최소 한 개의 연락처를 입력해 주세요.
              </ListItem>
            </ul>
          </Box>
        </DetailInfoItem>
        {!isIndividual && (
          <>
            <DetailInfoItem name="담당자 이름">
              <Input
                placeholder="담당자 이름"
                {...register(`participants.${id}.personInCharge.name`)}
              />
              {!externalSigner && (
                <Tooltip content="계약 정보에 입력된 담당자 정보를 불러옵니다." placement="bottom">
                  <TextButton
                    ml={6}
                    sx={{ flex: 'none' }}
                    onClick={() => {
                      const personInCharge = getPersonInCharge();

                      if (!personInCharge) {
                        Toast.warning({ description: '불러올 정보가 없습니다.' });
                      } else {
                        if (personInCharge.name) {
                          setValue(`participants.${id}.personInCharge.name`, personInCharge.name);
                        }
                        if (personInCharge.email) {
                          setValue(`participants.${id}.personInCharge.email`, personInCharge.email);
                        }
                        if (personInCharge.phone) {
                          setValue(`participants.${id}.personInCharge.phone`, personInCharge.phone);
                        }
                        trigger(`participants.${id}.personInCharge`);
                        Toast.success({ description: '담당자 정보를 불러왔습니다.' });
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faRightFromBracket}
                      style={{ marginRight: '2px', fontSize: '12px' }}
                    />
                    정보 불러오기
                  </TextButton>
                </Tooltip>
              )}
            </DetailInfoItem>
            <DetailInfoItem name="담당자 연락처">
              <Flex flexDirection={['column', null, 'row']} width="100%">
                <Box flex="1 1 0" mb={[3, null, 0]} mr={[null, null, 3]}>
                  <Input
                    isInvalid={!!errors.participants?.[id]?.personInCharge?.email?.message}
                    placeholder="이메일 주소"
                    {...register(`participants.${id}.personInCharge.email`, {
                      validate: (value) => {
                        const emailValidation = validateEmail(value);
                        return value ? emailValidation.success || emailValidation.message : true;
                      },
                    })}
                  />
                  {errors.participants?.[id]?.personInCharge?.email?.message && (
                    <ValidationMessage>
                      {errors.participants?.[id]?.personInCharge?.email?.message}
                    </ValidationMessage>
                  )}
                </Box>
                <Controller
                  defaultValue=""
                  name={`participants.${id}.personInCharge.phone`}
                  render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                    <Box flex="1 1 0">
                      <InternationalTelephoneInput
                        isInvalid={invalid}
                        value={value}
                        onChange={onChange}
                      />
                      {errors.participants?.[id]?.personInCharge?.phone?.message && (
                        <ValidationMessage>
                          {errors.participants?.[id]?.personInCharge?.phone?.message}
                        </ValidationMessage>
                      )}
                    </Box>
                  )}
                  rules={{
                    validate: (value) =>
                      !value ||
                      (!isValidPhoneNumber(value) ? '정확한 번호를 입력해 주세요.' : true),
                  }}
                />
              </Flex>
            </DetailInfoItem>
          </>
        )}
      </Flex>
    </Box>
  );
}
